import { useLayoutEffect, useRef, useState } from 'react';

/**
 * portal-credify#179 (Taras 10-30): the note a client left when booking from the portal
 * ("Anything you'd like … to know?"), under that appointment on the client's Appointments tab.
 * The API sends only the client's part of the Calendar description (`clientNote`). It renders as
 * text (React escapes it), wrapped, collapsed to about 2 lines with Show more when it's longer.
 */

/** A first guess before the line is measured: long or multi-line notes start collapsible. */
export const looksLong = (note: string) => note.length > 160 || note.split('\n').length > 2;

export function ApptClientNoteIcon() {
  return (
    <svg
      className="appt-note-icon"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="The client left a note"
    >
      <title>The client left a note</title>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="13" x2="13" y2="13" />
    </svg>
  );
}

export function ApptClientNote({ note }: { note: string }) {
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(() => looksLong(note));
  const ref = useRef<HTMLDivElement>(null);
  // Measured while collapsed: Show more only when the note really runs past 2 lines.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    setLong(el.scrollHeight > el.clientHeight + 1);
  }, [note, open]);
  return (
    <div className="appt-client-note">
      <div ref={ref} className={`appt-client-note-body${open ? '' : ' is-clamped'}`}>
        <span className="appt-client-note-label">Client&rsquo;s note:</span> {note}
      </div>
      {(long || open) && (
        <button
          type="button"
          className="appt-client-note-more"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
